// Global Instructions Rule Applied!

import React, { forwardRef } from 'react'
import { InputNumber } from 'antd'
import { InputSize, FieldTypeEnum, FieldValue } from '../types.ts'
import { createNumberInputFormatter, parseNumberInput } from '../utils/formatters.ts'

export interface NumberInputProps {
  value: FieldValue
  onChange: (value: number | null) => void
  onKeyDown?: (e: React.KeyboardEvent) => void
  size?: InputSize
  status?: 'error' | 'warning'
  placeholder?: string
  min?: number
  max?: number
  step?: number
  formatter?: (value?: number | string, info?: { userTyping: boolean; input: string }) => string
  /** antd InputNumber parser: the display value may be undefined while the input is empty. */
  parser?: (value: string | undefined) => string
  currencySymbol?: string
  fieldType: FieldTypeEnum.NUMBER | FieldTypeEnum.CURRENCY
  autoFocus?: boolean
}

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      value,
      onChange,
      onKeyDown,
      size = 'small',
      status,
      placeholder,
      min,
      max,
      step,
      formatter,
      parser,
      currencySymbol,
      fieldType,
      autoFocus
    },
    ref
  ) => {
    const defaultFormatter = createNumberInputFormatter()
    const defaultParser = parseNumberInput

    // Convert number value to string for stringMode
    const stringValue = value !== null && value !== undefined ? String(value) : null

    // Convert string value back to number in onChange
    const handleChange = (val: string | null) => {
      if (val === null || val === '') {
        onChange(null)
        return
      }
      const parsed = parseFloat(val)
      onChange(isNaN(parsed) ? null : parsed)
    }

    const shouldUseFormatter = fieldType === FieldTypeEnum.CURRENCY || formatter
    const finalFormatter = formatter || (shouldUseFormatter ? defaultFormatter : undefined)
    const finalParser = parser || (shouldUseFormatter ? defaultParser : undefined)

    return (
      <InputNumber
        ref={ref}
        stringMode
        value={stringValue}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        size={size}
        status={status}
        placeholder={placeholder}
        min={min === undefined ? undefined : String(min)}
        max={max === undefined ? undefined : String(max)}
        step={step}
        formatter={finalFormatter}
        parser={finalParser}
        addonBefore={fieldType === FieldTypeEnum.CURRENCY ? currencySymbol : undefined}
        style={{ width: '100%' }}
        autoFocus={autoFocus}
      />
    )
  }
)

NumberInput.displayName = 'NumberInput'
